import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader } from '../components/Card';
import Button from '../components/Button';
import { useDashboardStore } from '../store/dashboardStore';
import { apiService } from '../services/api';
import { PricingPlan, CreateSubscriptionResponse, PaymentMethod } from '../types';
import {
  CheckIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const SubscriptionPage: React.FC = () => {
  const { subscription, fetchSubscription, updateSubscription } = useDashboardStore();
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);

  useEffect(() => {
    fetchSubscription();
    fetchPricingPlans();
    fetchPaymentMethod();
  }, [fetchSubscription]);

  const fetchPricingPlans = async () => {
    try {
      const response = await apiService.getSubscriptionPricingPlans();
      if (response.success && response.data) {
        setPricingPlans(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
    }
  };

  const fetchPaymentMethod = async () => {
    try {
      const response = await apiService.getPaymentMethod();
      if (response.success && response.data) {
        setPaymentMethod(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch payment method:', error);
    }
  };

  const handleSubscribe = async (planId: string) => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrors('');

    try {
      const response = await apiService.createSubscription(planId, 'payment-method-id');
      if (response.success && response.data) {
        const subscriptionData = response.data as CreateSubscriptionResponse;
        
        if (subscriptionData.paymentUrl) {
          window.open(subscriptionData.paymentUrl, '_blank');
        }
        
        if (subscriptionData.subscription) {
          updateSubscription(subscriptionData.subscription);
        }
        
        setSuccessMessage('Subscription updated successfully!');
      }
    } catch (error: any) {
      setErrors(error.message || 'Failed to update subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setErrors('');

    try {
      const response = await apiService.cancelSubscription();
      if (response.success) {
        setSuccessMessage('Subscription cancelled successfully');
        fetchSubscription();
      }
    } catch (error: any) {
      setErrors(error.message || 'Failed to cancel subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Success Message */}
        {successMessage && (
          <div className="rounded-md bg-green-50 p-4">
            <div className="text-sm text-green-800">{successMessage}</div>
          </div>
        )}

        {/* Error Message */}
        {errors && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{errors}</div>
          </div>
        )}

        {/* Current Subscription */}
        {subscription && (
          <Card>
            <CardHeader>
              <h3 className="text-lg font-medium text-gray-900">Current Subscription</h3>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Plan</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">
                    {subscription.plan}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${subscription.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}
                  >
                    {subscription.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Next Billing</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(subscription.endDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Auto-renew</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {subscription.autoRenew ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>

              {subscription.status === 'active' && (
                <div className="mt-6 flex justify-end">
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    isLoading={isLoading}
                  >
                    Cancel Subscription
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pricing Plans */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-6">Available Plans</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pricingPlans.map((plan) => {
              // Check if this is the current plan
              const isCurrentPlan = subscription?.planId === plan.id && subscription.status === 'active';

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-lg border ${isCurrentPlan
                    ? 'border-primary-500 ring-2 ring-primary-500'
                    : 'border-gray-200'
                    } bg-white p-6 flex flex-col`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="inline-flex rounded-full bg-primary-500 px-4 py-1 text-sm font-semibold text-white">
                        Recommended
                      </span>
                    </div>
                  )}
                  {isCurrentPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="inline-flex rounded-full bg-green-500 px-4 py-1 text-sm font-semibold text-white">
                        Current Plan
                      </span>
                    </div>
                  )}

                  <h3 className="text-center text-2xl font-semibold text-gray-900">
                    {plan.name}
                  </h3>
                  <p className="text-center text-sm text-gray-500 mt-2 min-h-[40px]">{plan.description}</p>
                  <p className="mt-4 text-center">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {formatCurrency(plan.price)}
                    </span>
                    <span className="text-base text-gray-500">/{plan.billingCycle === 'monthly' ? 'monthly' : 'yearly'}</span>
                  </p>

                  <ul className="mt-6 space-y-4 flex-1">
                    {(plan.features || []).map((feature, idx) => (
                      <li key={idx} className="flex">
                        <CheckIcon className="flex-shrink-0 h-6 w-6 text-green-500" />
                        <span className="ml-3 text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    {isCurrentPlan ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        disabled
                      >
                        Current Plan
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        onClick={() => handleSubscribe(plan.id)}
                        isLoading={isLoading}
                      >
                        Subscribe
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Payment Method */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">Payment Method</h3>
          </CardHeader>
          <CardContent>
            {paymentMethod && paymentMethod.masked ?  (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CreditCardIcon className="h-8 w-8 text-gray-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">
                      Credit Card ending in {paymentMethod.masked.slice(paymentMethod.masked.length-4,paymentMethod.masked.length)}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  Update
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No payment method history available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Billing History */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">Billing History</h3>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No billing history available</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionPage;